// packages/shared/src/utils/mediaUrls.js

// -------------------------------------------------------
// Environment
// -------------------------------------------------------
import { getAxiosBaseURL } from '../config/axios';

const MEDIA_CDN_BASE_URL = (
  process.env.EXPO_PUBLIC_MEDIA_CDN_BASE_URL ||
  process.env.REACT_APP_CLOUDFRONT_DOMAIN ||
  process.env.CLOUDFRONT_DOMAIN ||
  ''
)
  .trim()
  .replace(/\/+$/, '');

const STORAGE_DRIVER = (
  process.env.EXPO_PUBLIC_STORAGE_DRIVER ||
  process.env.REACT_APP_STORAGE_DRIVER ||
  process.env.STORAGE_DRIVER ||
  (MEDIA_CDN_BASE_URL ? 's3' : 'local')
)
  .trim()
  .toLowerCase();

const USE_S3 = STORAGE_DRIVER === 's3';

const CLOUDFRONT_DOMAIN = MEDIA_CDN_BASE_URL.replace(
  /^https?:\/\//i,
  ''
).replace(/\/+$/, '');

const API_BASE_URL = (
  getAxiosBaseURL() ||
  process.env.REACT_APP_API_URL ||
  process.env.EXPO_PUBLIC_API_URL ||
  ''
)
  .replace(/\/api\/?$/, '')
  .replace(/\/+$/, '');

const AVATAR_UPLOADS_PREFIX =
  process.env.REACT_APP_AVATAR_UPLOADS_PREFIX ||
  process.env.EXPO_PUBLIC_AVATAR_UPLOADS_PREFIX ||
  '/uploads/users';

const ATTACHMENT_UPLOADS_PREFIX =
  process.env.REACT_APP_ATTACHMENT_UPLOADS_PREFIX ||
  process.env.EXPO_PUBLIC_ATTACHMENT_UPLOADS_PREFIX ||
  '/uploads/attachments';

// -------------------------------------------------------
// Helpers
// -------------------------------------------------------
function stripTrailingSlash(value = '') {
  return String(value).replace(/\/+$/, '');
}

function appendCacheBust(url, cacheBust = 0) {
  if (!cacheBust) return url;

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${encodeURIComponent(cacheBust)}`;
}

function isAbsoluteUrl(url) {
  return /^https?:\/\//i.test(url);
}

function normalizeKey(key) {
  if (!key) return '';
  return String(key).replace(/^\/+/, '');
}

function buildCloudFrontUrl(rawKey) {
  if (!rawKey) return '';

  if (isAbsoluteUrl(rawKey)) {
    return rawKey;
  }

  const key = normalizeKey(rawKey);

  if (!CLOUDFRONT_DOMAIN) {
    // Safety: if CF isn’t configured but USE_S3 is true,
    // fall back to API_BASE_URL / key
    if (API_BASE_URL) return `${API_BASE_URL}/${key}`;
    return `/${key}`;
  }

  return `https://${CLOUDFRONT_DOMAIN}/${key}`;
}

// -------------------------------------------------------
// Public helpers
// -------------------------------------------------------

/**
 * Build avatar URL.
 *
 * DB value for avatars should be the S3 key or filename you store as
 * `profile_picture`, e.g.:
 *   "avatars/1-avatar-1764903312984.jpg"  (preferred)
 *
 * If STORAGE_DRIVER = s3 → CloudFront URL
 * else                 → API_BASE_URL + /avatars/<file>
 */
export function buildAvatarUrl(profilePictureKey, cacheBust = 0) {
  if (!profilePictureKey) return '';

  const raw = String(profilePictureKey).trim();
  if (!raw) return '';

  if (isAbsoluteUrl(raw)) {
    return appendCacheBust(raw, cacheBust);
  }

  let key = normalizeKey(raw);

  if (USE_S3) {
    if (!key.includes('/')) {
      key = `avatars/${key}`;
    }

    return appendCacheBust(buildCloudFrontUrl(key), cacheBust);
  }

  const prefix = AVATAR_UPLOADS_PREFIX.startsWith('/')
    ? AVATAR_UPLOADS_PREFIX
    : `/${AVATAR_UPLOADS_PREFIX}`;

  const normalizedPrefix = normalizeKey(prefix);

  // Avoid duplicating uploads/users if it is already stored in the DB.
  if (key.startsWith(`${normalizedPrefix}/`)) {
    key = key.slice(normalizedPrefix.length + 1);
  }

  const url = API_BASE_URL
    ? `${API_BASE_URL}${prefix}/${key}`
    : `${prefix}/${key}`;

  return appendCacheBust(url, cacheBust);
}

/**
 * Build attachment URL for any direct-link use.
 *
 * For STORAGE_DRIVER = s3:
 *   - Expect full S3 key like "user_1/entry_441/uuid.png"
 *   - Returns CloudFront URL.
 *
 * For STORAGE_DRIVER != s3:
 *   - Expect local filename (or key under /uploads/attachments)
 *   - Returns API_BASE_URL + /uploads/attachments/<file>
 *
 * NOTE: For secure viewing/downloading you are still using
 *       /api/attachments/:id/signed-url, which is good.
 */
export function buildAttachmentUrl(attachmentKey) {
  if (!attachmentKey) return '';

  const raw = String(attachmentKey).trim();
  if (!raw) return '';

  if (isAbsoluteUrl(raw)) {
    return raw;
  }

  const key = normalizeKey(raw);

  if (USE_S3) {
    return buildCloudFrontUrl(key);
  }

  const prefix = ATTACHMENT_UPLOADS_PREFIX.startsWith('/')
    ? ATTACHMENT_UPLOADS_PREFIX
    : `/${ATTACHMENT_UPLOADS_PREFIX}`;

  const normalizedPrefix = normalizeKey(prefix);
  const localKey = key.startsWith(`${normalizedPrefix}/`)
    ? key.slice(normalizedPrefix.length + 1)
    : key;

  if (API_BASE_URL) {
    return `${API_BASE_URL}${prefix}/${localKey}`;
  }

  return `${prefix}/${localKey}`;
}
